import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isValid as isDateValid,
  isSameDay,
  isSameMonth,
  parse,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { fr } from 'date-fns/locale'
import { AnimatePresence, motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useTheme } from '../../theme/hooks'
import { colors } from '../../theme/theme'

type DateRange = { start: string; end: string }

type CalendarProps = {
  mode?: 'single' | 'range'
  value: string | DateRange | undefined
  onChange: (value: string | DateRange) => void
  focusField?: 'start' | 'end'
  demandData?: Record<string, 'red' | 'yellow' | 'green'>
  disablePastDates?: boolean
  disableFutureDates?: boolean
  minDate?: string
  maxDate?: string
}

export const toISO = (date: Date) => format(date, 'yyyy-MM-dd')
export const toDisplayFR = (iso: string) =>
  format(parseISO(iso), 'dd/MM/yyyy', { locale: fr })

export const isDateOutsideBounds = (
  date: Date,
  minDate?: string,
  maxDate?: string,
): boolean => {
  const iso = format(date, 'yyyy-MM-dd')
  if (minDate && iso < minDate) return true
  if (maxDate && iso > maxDate) return true
  return false
}

export default function Calendar({
  value,
  onChange,
  demandData = {},
  mode = 'single',
  focusField = 'start',
  disablePastDates = false,
  disableFutureDates = false,
  minDate = undefined,
  maxDate = undefined,
}: CalendarProps) {
  const { theme, isDark } = useTheme()
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    let initialDateSource: string | undefined

    if (typeof value === 'string') {
      initialDateSource = value
    } else if (typeof value === 'object' && value?.start) {
      initialDateSource = value.start
    }

    if (initialDateSource) {
      const dateCandidate = new Date(initialDateSource + 'T00:00:00Z')
      if (isDateValid(dateCandidate)) {
        return dateCandidate
      } else {
        console.warn(
          '[Calendar Init] Invalid date string received in value:',
          initialDateSource,
        )
      }
    }
    return new Date()
  })

  const [direction, setDirection] = useState<number>(0)
  const [view, setView] = useState<'days' | 'months' | 'years'>('days')
  const [zoomDirection, setZoomDirection] = useState<'in' | 'out' | null>(null)
  const [rangeStartInternal, setRangeStartInternal] = useState<Date | null>(
    null,
  )
  const [selectingStep, setSelectingStep] = useState<'start' | 'end'>(() => {
    if (
      mode === 'range' &&
      typeof value === 'object' &&
      value?.start &&
      !value?.end
    ) {
      return 'end'
    }
    return 'start'
  })

  useEffect(() => {
    if (mode === 'range') {
      const rangeValue =
        typeof value === 'object' && value ? value : null
      if (rangeValue?.start && rangeValue?.end) {
        setSelectingStep('start')
        setRangeStartInternal(null)
      } else if (rangeValue?.start && !rangeValue?.end) {
        setSelectingStep('end')
        if (rangeValue.start) {
          const parsed = parseISO(rangeValue.start)
          if (isDateValid(parsed)) {
            setRangeStartInternal(parsed)
          }
        }
      } else if (!rangeValue?.start && rangeValue?.end) {
        setSelectingStep('start')
        setRangeStartInternal(null)
      } else {
        setSelectingStep(focusField === 'end' ? 'end' : 'start')
        setRangeStartInternal(null)
      }
    }
  }, [mode, value, focusField])

  const handleSelectDate = (selected: Date) => {
    if (!isDateValid(selected)) return

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (disablePastDates && selected < today) return
    if (disableFutureDates && selected > today) return
    if (isDateOutsideBounds(selected, minDate, maxDate)) return

    const formatted = format(selected, 'yyyy-MM-dd')

    if (mode === 'range') {
      if (selectingStep === 'start') {
        setRangeStartInternal(selected)
        onChange({ start: formatted, end: '' })
        setSelectingStep('end')
      } else {
        const startDate = rangeStartInternal || selected
        const endDate = selected
        const [s, e] =
          startDate <= endDate ? [startDate, endDate] : [endDate, startDate]
        onChange({
          start: format(s, 'yyyy-MM-dd'),
          end: format(e, 'yyyy-MM-dd'),
        })
        setRangeStartInternal(null)
        setSelectingStep('start')
      }
    } else {
      onChange(formatted)
    }
  }

  const renderHeader = () => (
    <div className="mb-2 flex items-center justify-between">
      <button
        type="button"
        className="transition-transform hover:scale-150"
        style={{
          color: theme.text.primary,
          backgroundColor: 'transparent',
        }}
        onMouseEnter={e =>
          (e.currentTarget.style.color = colors.blue[500])
        }
        onMouseLeave={e =>
          (e.currentTarget.style.color = theme.text.primary)
        }
        onClick={() => {
          if (!isDateValid(currentMonth)) return
          if (view === 'days') {
            setDirection(-1)
            setCurrentMonth(subMonths(currentMonth, 1))
          } else if (view === 'years') {
            setCurrentMonth(new Date(currentMonth.getFullYear() - 12, 0, 1))
          } else if (view === 'months') {
            setCurrentMonth(new Date(currentMonth.getFullYear() - 1, 0, 1))
          }
        }}
      >
        {'<'}
      </button>
      <div
        className="cursor-pointer text-lg font-semibold transition-transform hover:scale-105"
        style={{ color: theme.text.primary }}
        onClick={() => {
          if (!isDateValid(currentMonth)) return // Prevent action on invalid date
          setZoomDirection('out')
          setView(prev =>
            prev === 'days' ? 'months' : prev === 'months' ? 'years' : 'years',
          )
        }}
      >
        {/* Add checks here before formatting */}
        {
          isDateValid(currentMonth)
            ? view === 'days'
              ? format(currentMonth, 'MMMM yyyy', { locale: fr })
              : view === 'months'
                ? format(currentMonth, 'yyyy', { locale: fr })
                : `${currentMonth.getFullYear() - 6} - ${currentMonth.getFullYear() + 5}`
            : 'Date Invalide' // Display fallback text
        }
      </div>
      <button
        type="button"
        className="transition-transform hover:scale-150"
        style={{
          color: theme.text.primary,
          backgroundColor: 'transparent',
        }}
        onMouseEnter={e =>
          (e.currentTarget.style.color = colors.blue[500])
        }
        onMouseLeave={e =>
          (e.currentTarget.style.color = theme.text.primary)
        }
        onClick={() => {
          if (!isDateValid(currentMonth)) return // Prevent action on invalid date
          // Logic for next month/year
          if (view === 'days') {
            setDirection(1)
            setCurrentMonth(addMonths(currentMonth, 1))
          } else if (view === 'years') {
            setCurrentMonth(new Date(currentMonth.getFullYear() + 12, 0, 1))
          } else if (view === 'months') {
            setCurrentMonth(new Date(currentMonth.getFullYear() + 1, 0, 1))
          }
        }}
      >
        {'>'}
      </button>
    </div>
  )

  // ... (renderDays function remains the same) ...
  const renderDays = () => {
    const startDate = startOfWeek(currentMonth, { locale: fr })
    return (
      <div className="mb-1 grid grid-cols-7">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="text-center text-sm font-medium"
            style={{ color: theme.text.secondary }}
          >
            {isDateValid(startDate)
              ? format(addDays(startDate, i), 'EEEEE', { locale: fr })
              : '-'}
          </div>
        ))}
      </div>
    )
  }

  const renderCells = () => {
    // Add safety check for currentMonth at the beginning
    if (!isDateValid(currentMonth)) {
      return (
        <div className="p-4 text-center text-red-500">
          Erreur: Mois courant invalide.
        </div>
      )
    }

    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart, { locale: fr })
    const endDate = endOfWeek(monthEnd, { locale: fr })

    // Add checks if startDate or endDate become invalid
    if (!isDateValid(startDate) || !isDateValid(endDate)) {
      return (
        <div className="p-4 text-center text-red-500">
          Erreur: Plage de dates invalide pour le rendu.
        </div>
      )
    }

    const rows: JSX.Element[] = []
    let days: JSX.Element[] = []
    let day = startDate

    const isDateRange = (
      val: string | DateRange | undefined,
    ): val is DateRange =>
      typeof val === 'object' && val !== null && 'start' in val && 'end' in val

    // Parse dates robustly
    let parsedStartDate: Date | null = null
    let parsedEndDate: Date | null = null
    let parsedSingleDate: Date | null = null

    try {
      if (mode === 'range' && isDateRange(value)) {
        const parseAnyDate = (dateStr: string) => {
          if (!dateStr) return null
          if (dateStr.includes('-')) {
            const parsed = parseISO(dateStr)
            return isDateValid(parsed) ? parsed : null
          }
          if (dateStr.includes('/')) {
            const parsed = parse(dateStr, 'dd/MM/yyyy', new Date())
            return isDateValid(parsed) ? parsed : null
          }
          return null
        }

        parsedStartDate = parseAnyDate(value.start)
        parsedEndDate = parseAnyDate(value.end)
      } else if (mode === 'single' && typeof value === 'string') {
        const parsed = parseISO(value)
        if (isDateValid(parsed)) {
          parsedSingleDate = parsed
        }
      }
    } catch (e) {
      console.error('Error parsing value prop in renderCells', e)
      parsedStartDate = null
      parsedEndDate = null
      parsedSingleDate = null
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0) // Reset time to start of day

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const thisDay = day
        if (!isDateValid(thisDay)) {
          // Skip if day becomes invalid
          day = addDays(day, 1)
          continue
        }

        const formattedDate = format(thisDay, 'yyyy-MM-dd')
        const isToday = isSameDay(thisDay, new Date())
        const isCurrentMonth = isSameMonth(thisDay, monthStart)
        // Calculate isPastDate only if disablePastDates is true
        const isPastDate = disablePastDates && thisDay < today
            // Calculate isFutureDate only if disableFutureDates is true
            const isFutureDate = disableFutureDates && thisDay > today
        // Calculate isOutOfBounds only if min/max bounds are provided
        const isOutOfBounds = isDateOutsideBounds(thisDay, minDate, maxDate)

        // --- Selection Logic (using pre-parsed, validated dates) ---
        let isSelected = false
        let isRangeStart = false
        let isRangeEnd = false
        let isInRange = false

        if (mode === 'single' && parsedSingleDate) {
          isSelected = isSameDay(thisDay, parsedSingleDate)
        } else if (mode === 'range') {
          const effectiveStartDate = rangeStartInternal || parsedStartDate
          if (effectiveStartDate) {
            isRangeStart = isSameDay(thisDay, effectiveStartDate)
          }
          if (parsedEndDate) {
            isRangeEnd = isSameDay(thisDay, parsedEndDate)
          }
          if (effectiveStartDate && parsedEndDate) {
            if (effectiveStartDate < parsedEndDate) {
              isInRange = thisDay > effectiveStartDate && thisDay < parsedEndDate
            } else {
              isInRange = thisDay < effectiveStartDate && thisDay > parsedEndDate
            }
          }
        }
        // --- End Selection Logic ---

        const dotColor = demandData[formattedDate]

        // Determine colors based on state
        let dayBgColor = 'transparent'
        let dayTextColor = theme.text.primary
        let dayBorderColor = 'transparent'
        let dayBorderWidth = '0px'

        if (isSelected || isRangeStart || isRangeEnd) {
          dayBgColor = isDark
            ? 'rgba(59, 130, 246, 0.3)' // blue-500/30
            : 'rgba(59, 130, 246, 0.15)' // blue-500/15
          dayBorderColor = colors.blue[500]
          dayBorderWidth = '2px'
          dayTextColor = colors.blue[700]
        } else if (isToday) {
          dayBorderColor = colors.blue[400]
          dayBorderWidth = '1px'
          dayTextColor = theme.text.primary
        }

        if (isInRange) {
          dayBgColor = isDark
            ? 'rgba(59, 130, 246, 0.2)' // blue-500/20
            : theme.status.info.bg
          dayTextColor = colors.blue[600]
        }

        if (isPastDate || isFutureDate || isOutOfBounds) {
          dayTextColor = theme.text.disabled
        } else if (!isCurrentMonth) {
          dayTextColor = theme.text.tertiary
        }

        const isDisabled = isPastDate || isFutureDate || isOutOfBounds

        days.push(
          <motion.div
            key={thisDay.toISOString()}
            whileHover={{ scale: isDisabled ? 1 : 1.05 }}
            whileTap={{ scale: isDisabled ? 1 : 0.95 }}
            onClick={() => !isDisabled && handleSelectDate(thisDay)}
            className={`relative flex h-10 w-10 flex-col items-center justify-center rounded-full text-sm transition-colors ${
              isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
            style={{
              backgroundColor: dayBgColor,
              color: dayTextColor,
              borderColor: dayBorderColor,
              borderWidth: dayBorderWidth,
              borderStyle: dayBorderWidth !== '0px' ? 'solid' : 'none',
            }}
            onMouseEnter={e => {
              if (!isDisabled) {
                e.currentTarget.style.backgroundColor = theme.background.hover
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = dayBgColor
            }}
          >
            <span className="leading-none">
              {format(thisDay, 'd', { locale: fr })}
            </span>
            <span
              className="mt-1 h-2 w-2 rounded-full"
              style={{
                backgroundColor: dotColor
                  ? dotColor === 'green'
                    ? '#69DB7C'
                    : dotColor === 'yellow'
                      ? '#FFE066'
                      : dotColor === 'red'
                        ? '#FF6B6B'
                        : 'transparent'
                  : 'transparent',
                visibility: 'visible',
              }}
            />
          </motion.div>,
        )
        day = addDays(day, 1)
      }
      rows.push(
        <div
          key={format(addDays(day, -7), 'yyyy-MM-dd')}
          className="flex justify-between gap-1"
        >
          {days}
        </div>,
      )
      days = []
    }
    return <div className="space-y-1">{rows}</div>
  }

  const handleSelectMonth = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1))
    setZoomDirection('in')
    setView('days')
  }
  const handleSelectYear = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1))
    setZoomDirection('in')
    setView('months')
  }
  const renderMonthGrid = () => (
    <div className="grid grid-cols-3 gap-2">
      {' '}
      {Array.from({ length: 12 }, (_, i) => (
        <div
          key={i}
          className="cursor-pointer rounded-lg p-3 text-center transition-colors"
          style={{
            color: theme.text.primary,
            backgroundColor: 'transparent',
          }}
          onMouseEnter={e =>
            (e.currentTarget.style.backgroundColor = theme.background.hover)
          }
          onMouseLeave={e =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
          onClick={() => handleSelectMonth(i)}
        >
          {' '}
          {format(new Date(2025, i, 1), 'MMM', { locale: fr })}{' '}
        </div>
      ))}{' '}
    </div>
  )
  const renderYearGrid = () => {
    const startYear = currentMonth.getFullYear() - 6
    return (
      <div className="grid grid-cols-3 gap-2">
        {' '}
        {Array.from({ length: 12 }, (_, i) => startYear + i).map(year => (
          <div
            key={year}
            className="cursor-pointer rounded-lg p-3 text-center transition-colors"
            style={{
              color: theme.text.primary,
              backgroundColor: 'transparent',
            }}
            onMouseEnter={e =>
              (e.currentTarget.style.backgroundColor = theme.background.hover)
            }
            onMouseLeave={e =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
            onClick={() => handleSelectYear(year)}
          >
            {' '}
            {year}{' '}
          </div>
        ))}{' '}
      </div>
    )
  }

  const getAnimationProps = () => {
    if (view === 'days') {
      return {
        initial: { x: direction > 0 ? 100 : -100, opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: direction > 0 ? -100 : 100, opacity: 0 },
      }
    } else {
      return {
        initial:
          zoomDirection === 'out'
            ? { scale: 1.2, opacity: 0 }
            : { scale: 0.8, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        exit:
          zoomDirection === 'out'
            ? { scale: 0.8, opacity: 0 }
            : { scale: 1.2, opacity: 0 },
      }
    }
  }

  return (
    <div
      className="w-full rounded-lg p-4"
      style={{
        backgroundColor: theme.surface.primary,
        boxShadow: theme.card.shadow,
      }}
    >
      {' '}
      {renderHeader()}
      {view === 'days' && renderDays()}
      <AnimatePresence mode="wait">
        <motion.div
          key={
            view +
            (isDateValid(currentMonth) ? currentMonth.toISOString() : 'invalid')
          }
          {...getAnimationProps()}
          transition={{ duration: 0.3 }}
        >
          {view === 'days' && renderCells()}
          {view === 'months' && renderMonthGrid()}
          {view === 'years' && renderYearGrid()}
        </motion.div>
      </AnimatePresence>
      {mode === 'range' && view === 'days' && (
        <p
          className="mt-2 text-center text-xs italic"
          style={{ color: theme.text.tertiary }}
        >
          {selectingStep === 'end'
            ? 'Cliquez sur une date pour définir la date de fin'
            : 'Cliquez sur une date pour définir la date de début'}
        </p>
      )}
    </div>
  )
}
